/**
 * Phase 5-B — PulseDrawer.
 *
 * Right-side slide-over chat UI. Shadcn `Sheet` wrapped so we can style the
 * header + body independently. Composition:
 *   [ header: avatar + name + mute toggle + close ]
 *   [ messages scroll ]
 *   [ quick-action chip strip ]
 *   [ input + send ]
 */

import { AnimatePresence, motion } from "framer-motion";
import {
    Bell,
    BellOff,
    Clock,
    Hash,
    Loader2,
    MessageCircle,
    Pencil,
    Send,
    Sparkles,
    TrendingUp,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import PulseAvatar from "@/components/pulse/PulseAvatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { usePulse } from "@/contexts/PulseContext";
import { useSession } from "@/contexts/SessionContext";

const PULSE_COST = 2;

/* --------------------- quick action chip definitions -------------------- */

const BASE_CHIPS = [
    {
        id: "where-to-post",
        label: "Where should I post this?",
        icon: MessageCircle,
    },
    {
        id: "why-tuesday",
        label: "Why is Tuesday a good day?",
        icon: Clock,
    },
    {
        id: "suggest-hashtags",
        label: "Suggest hashtags for my current asset",
        icon: Hash,
    },
    {
        id: "improve-last-title",
        label: "Improve my last title",
        icon: Pencil,
    },
    {
        id: "whats-trending",
        label: "What's trending for my niche?",
        icon: TrendingUp,
    },
];

const PRO_ONLY_CHIP = {
    id: "best-time-to-post",
    label: "Best time to post (Pro)",
    icon: Clock,
    pro: true,
};

/* --------------------------- helper formatters -------------------------- */

function fmtTime(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

// Very light markdown-ish bold handling for ** fragments.
function renderInlineBold(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
            return (
                <strong
                    key={i}
                    className="font-semibold text-[#00F5D4]"
                >
                    {p.slice(2, -2)}
                </strong>
            );
        }
        return <span key={i}>{p}</span>;
    });
}

/* -------------------------------- Drawer ------------------------------- */

export default function PulseDrawer() {
    const { tier, balance } = useSession();
    const {
        drawerOpen,
        closeDrawer,
        setDrawerOpen,
        messages,
        sending,
        send,
        sendQuickAction,
        tipMuted,
        setTipMuted,
    } = usePulse();

    const [input, setInput] = useState("");
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const chips = [
        ...BASE_CHIPS,
        ...(tier === "pro" ? [PRO_ONLY_CHIP] : []),
    ];

    // Latest-reply followups → render as temporary chip overlay above base chips
    const latestPulse = [...messages].reverse().find((m) => m.role === "pulse");
    const followups = latestPulse?.followups?.slice(0, 3) || [];

    // Auto-scroll to bottom on message add + when drawer opens.
    useEffect(() => {
        if (!drawerOpen) return;
        const el = scrollRef.current;
        if (el) {
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
            });
        }
    }, [messages, drawerOpen]);

    // Focus input on open.
    useEffect(() => {
        if (drawerOpen) {
            setTimeout(() => inputRef.current?.focus(), 180);
        }
    }, [drawerOpen]);

    const canAfford = balance >= PULSE_COST;

    const onSend = () => {
        if (!input.trim() || sending || !canAfford) return;
        send(input);
        setInput("");
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
                side="right"
                className="flex w-full flex-col gap-0 border-l border-[#1F2A40] bg-[#0B1220] p-0 text-[#E8ECF5] sm:max-w-[480px]"
                data-testid="pulse-drawer"
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 border-b border-[#1F2A40] px-4 py-3.5">
                    <div className="flex items-center gap-3">
                        <PulseAvatar size={34} data-testid="pulse-drawer-avatar" />
                        <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium text-[#E8ECF5]">
                                Pulse
                            </span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-[#8A93A6]">
                                AI partner · strategic advisor
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setTipMuted(!tipMuted)}
                            aria-label={
                                tipMuted
                                    ? "Unmute proactive tips"
                                    : "Mute proactive tips"
                            }
                            data-testid="pulse-mute-toggle"
                            data-muted={tipMuted ? "true" : "false"}
                            className={[
                                "inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors",
                                tipMuted
                                    ? "border-[#1F2A40] bg-[#111726] text-[#FF5470]"
                                    : "border-[#1F2A40] bg-[#111726] text-[#8A93A6] hover:text-[#00F5D4]",
                            ].join(" ")}
                        >
                            {tipMuted ? (
                                <BellOff className="h-3.5 w-3.5" />
                            ) : (
                                <Bell className="h-3.5 w-3.5" />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={closeDrawer}
                            aria-label="Close Pulse drawer"
                            data-testid="pulse-drawer-close"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#1F2A40] bg-[#111726] text-[#8A93A6] transition-colors hover:text-[#E8ECF5]"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto px-4 py-5"
                    data-testid="pulse-messages"
                >
                    {messages.length === 0 && (
                        <div
                            className="mt-6 flex flex-col items-center gap-3 text-center"
                            data-testid="pulse-empty-state"
                        >
                            <PulseAvatar size={56} showPulse />
                            <h3 className="text-base font-medium text-[#E8ECF5]">
                                Where should we publish today?
                            </h3>
                            <p className="max-w-xs text-xs leading-relaxed text-[#8A93A6]">
                                Ask Pulse about platform routing, hooks,
                                hashtags, timing — anything strategic. Each
                                free-form message is{" "}
                                <span className="font-mono text-[#00F5D4]">
                                    −{PULSE_COST} NXT
                                </span>
                                . Quick-action chips are shortcuts.
                            </p>
                        </div>
                    )}

                    <AnimatePresence initial={false}>
                        {messages.map((m) => (
                            <motion.div
                                key={m.id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                                className={[
                                    "mb-3 flex flex-col",
                                    m.role === "user" ? "items-end" : "items-start",
                                ].join(" ")}
                                data-testid={`pulse-msg-${m.role}`}
                            >
                                <div
                                    className={[
                                        "max-w-[86%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                                        m.role === "user"
                                            ? "rounded-br-md bg-[#6366F1]/18 text-[#E8ECF5]"
                                            : m.error
                                              ? "rounded-bl-md border border-[#FF5470]/40 bg-[#111726] text-[#FF5470]"
                                              : "rounded-bl-md border border-[#0EA5E9]/25 bg-[#111726] text-[#E8ECF5]",
                                    ].join(" ")}
                                >
                                    {renderInlineBold(m.content || "")}
                                </div>
                                <span className="mt-1 px-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A93A6]">
                                    {fmtTime(m.created_at)}
                                </span>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {sending && (
                        <div
                            className="mt-1 flex items-center gap-2 pl-2 text-xs text-[#8A93A6]"
                            data-testid="pulse-sending"
                        >
                            <Loader2 className="h-3 w-3 animate-spin text-[#00F5D4]" />
                            Pulse is thinking…
                        </div>
                    )}
                </div>

                {/* Quick actions */}
                <div
                    className="border-t border-[#1F2A40] px-3 py-2.5"
                    data-testid="pulse-quick-actions"
                >
                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {followups.length > 0
                            ? followups.map((text, i) => (
                                  <button
                                      key={`follow-${i}`}
                                      type="button"
                                      onClick={() => send(text)}
                                      disabled={sending || !canAfford}
                                      className="shrink-0 whitespace-nowrap rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/8 px-3 py-1 text-[11px] text-[#E8ECF5] transition-all hover:border-[#00F5D4] hover:bg-[#00F5D4]/15 disabled:cursor-not-allowed disabled:opacity-50"
                                      data-testid={`pulse-followup-${i}`}
                                  >
                                      {text}
                                  </button>
                              ))
                            : chips.map((c) => {
                                  const Icon = c.icon;
                                  return (
                                      <button
                                          key={c.id}
                                          type="button"
                                          onClick={() => sendQuickAction(c.id)}
                                          disabled={sending}
                                          className={[
                                              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition-all",
                                              c.pro
                                                  ? "border-[#00F5D4]/50 bg-[#00F5D4]/10 text-[#00F5D4] hover:bg-[#00F5D4]/20"
                                                  : "border-[#1F2A40] bg-[#111726] text-[#E8ECF5] hover:border-[#6366F1]/60 hover:text-[#E8ECF5]",
                                              sending &&
                                                  "cursor-not-allowed opacity-60",
                                          ].join(" ")}
                                          data-testid={`pulse-chip-${c.id}`}
                                          data-pro={c.pro ? "true" : "false"}
                                      >
                                          <Icon className="h-3 w-3" />
                                          {c.label}
                                      </button>
                                  );
                              })}
                    </div>
                </div>

                {/* Input */}
                <div className="border-t border-[#1F2A40] px-3 py-3">
                    <div className="flex items-end gap-2">
                        <textarea
                            ref={inputRef}
                            rows={2}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKey}
                            disabled={sending || !canAfford}
                            placeholder={
                                canAfford
                                    ? "Ask Pulse anything…"
                                    : "Not enough NXT — top up or upgrade"
                            }
                            data-testid="pulse-input"
                            className="min-h-[42px] flex-1 resize-none rounded-xl border border-[#1F2A40] bg-[#111726] px-3 py-2 text-sm text-[#E8ECF5] placeholder:text-[#8A93A6] transition-all focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]/50 disabled:cursor-not-allowed disabled:opacity-60"
                        />
                        <button
                            type="button"
                            onClick={onSend}
                            disabled={sending || !input.trim() || !canAfford}
                            data-testid="pulse-send-btn"
                            className={[
                                "inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl text-white transition-all",
                                sending || !input.trim() || !canAfford
                                    ? "cursor-not-allowed bg-[#1F2A40] opacity-60"
                                    : "bg-gradient-to-br from-[#6366F1] to-[#0EA5E9] hover:shadow-[0_0_18px_rgba(99,102,241,0.6)]",
                            ].join(" ")}
                            aria-label="Send message"
                        >
                            {sending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    <p
                        className="mt-1.5 flex items-center gap-1 px-1 font-mono text-[10px] tracking-[0.14em] text-[#8A93A6]"
                        data-testid="pulse-input-hint"
                    >
                        <Sparkles className="h-2.5 w-2.5" />
                        Each message{" "}
                        <span className="text-[#00F5D4]">
                            −{PULSE_COST} NXT
                        </span>
                        {" "}· Enter to send · Shift+Enter for newline
                    </p>
                </div>
            </SheetContent>
        </Sheet>
    );
}
